import {
  Box, Chip, Container, IconButton, Input, InputAdornment, Paper, Stack, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { EditOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { TrimText, getDeactivationInfo, formatDeactivationDays } from "../../utils/helpers";
import { ADMIN_ROLES } from "../../utils/constants";
import ViewUserManagement from "./ViewUser";

const INACTIVE_STATUS = 0;

const TABLE_STYLES = {
  headCells: {
    style: {
      fontSize: "15px",
      fontWeight: "bold",
      backgroundColor: "rgba(255, 255, 255, 0.02)",
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
      color: "black",
    },
  },
  rows: { style: { backgroundColor: "transparent", color: "white" } },
  cells: { style: { color: "white" } },
};

const getDaysLeft = (deactivation) => deactivation.waitingPeriodDays - deactivation.days;

// "less than 1 day" reads better than "0 days".
const formatInactiveFor = (days) => (days === 0 ? "less than 1 day" : formatDeactivationDays(days));

function WaitingPeriodChip({ user }) {
  const deactivation = getDeactivationInfo(user);

  if (!deactivation) return <Chip label="Unknown" size="small" />;
  if (deactivation.exceeded) return <Chip label="Passed" color="error" size="small" />;

  const daysLeft = formatDeactivationDays(getDaysLeft(deactivation));
  return <Chip label={`${daysLeft} left`} color="warning" size="small" />;
}

// Text for the delete confirm. It warns when the waiting period is not over yet.
const getDeleteWarning = (user) => {
  const deactivation = getDeactivationInfo(user);

  if (!deactivation) {
    return {
      title: "Inactive date unknown",
      text: "We don't know when this user became inactive, so the waiting period can't be checked. Delete anyway?",
      confirmText: "Delete anyway",
    };
  }

  const inactiveFor = formatInactiveFor(deactivation.days);
  const waitingPeriod = formatDeactivationDays(deactivation.waitingPeriodDays);

  if (!deactivation.exceeded) {
    const daysLeft = formatDeactivationDays(getDaysLeft(deactivation));
    return {
      title: "Waiting period not reached",
      text: `This user has been inactive for ${inactiveFor}. The waiting period is ${waitingPeriod} (${daysLeft} left). Delete anyway?`,
      confirmText: "Delete anyway",
    };
  }

  return {
    title: "Delete this user?",
    text: `This user has been inactive for ${inactiveFor}. The waiting period of ${waitingPeriod} has passed.`,
    confirmText: "Yes, delete",
  };
};

export default function InactiveUserList() {
  const { user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  // Same rule as the main users page.
  const canManage = currentUser?.role !== ADMIN_ROLES.JUNIOR;

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchItem, setSearchItem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  // Flipping this tells the table to go back to page 1.
  const [resetPage, setResetPage] = useState(false);
  const [viewId, setViewId] = useState("");

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const res = await userServices.getUser({
        searchItem,
        pageNumber: currentPage,
        pageSize,
        status: [INACTIVE_STATUS],
        sortBy: "deactivatedAt",
      });
      setData(res?.data?.data?.result || []);
      setTotalCount(res?.data?.data?.totalRecords || 0);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Waits a little so the search does not run on every key press.
  useEffect(() => {
    const timer = setTimeout(fetchUsers, 400);
    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchItem, refresh]);

  const handleSearchChange = (value) => {
    setSearchItem(value);
    setCurrentPage(1);
    setResetPage((prev) => !prev);
  };

  const handlePageSizeChange = (size, page) => {
    setPageSize(size);
    setCurrentPage(page);
  };

  const deleteUser = async (user) => {
    const warning = getDeleteWarning(user);
    const result = await Swal.fire({
      title: warning.title,
      text: warning.text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: warning.confirmText,
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await userServices.deleteUser({ id: user._id });
      toast.success(res?.data?.message, { toastId: "success" });
      setRefresh((prev) => !prev);
    } catch (err) {
      errorHandler(err);
    }
  };

  const renderInactiveFor = (row) => {
    const deactivation = getDeactivationInfo(row);
    if (!deactivation) return "-";

    return (
      <Typography
        variant="body2"
        sx={{ color: deactivation.exceeded ? "#ff5252" : "white", fontWeight: deactivation.exceeded ? 700 : 500 }}
      >
        {formatInactiveFor(deactivation.days)}
      </Typography>
    );
  };

  const renderActions = (row) => (
    <>
      <Tooltip title="View" placement="top">
        <IconButton color="primary" onClick={() => setViewId(row._id)}>
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      {canManage && (
        <Tooltip title="Edit" placement="top">
          <IconButton color="primary" onClick={() => navigate(`/user-management/edit/${row._id}`)}>
            <EditOutlined />
          </IconButton>
        </Tooltip>
      )}
      {canManage && (
        <Tooltip title="Delete" placement="top">
          <IconButton color="primary" onClick={() => deleteUser(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const columns = [
    { name: "S.No.", cell: (_, i) => (currentPage - 1) * pageSize + i + 1, width: "80px" },
    { name: "Username", selector: (row) => TrimText(row.username || "-", 25) },
    { name: "Email", selector: (row) => row.email || "-", grow: 2 },
    { name: "Country", selector: (row) => row.country || "-" },
    { name: "Inactive Since", selector: (row) => fDate(row.deactivatedAt) || "-" },
    { name: "Inactive For", cell: renderInactiveFor },
    { name: "Waiting Period", cell: (row) => <WaitingPeriodChip user={row} /> },
    { name: "Action", center: true, cell: renderActions, minWidth: "160px" },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet><title>Inactive Users</title></Helmet>
      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
            Inactive Users
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
            Users who have been inactive the longest are shown first. Delete a user after the waiting period has passed.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          <Input
            value={searchItem}
            placeholder="Search by name, username, email or phone..."
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ color: "white", minWidth: 320 }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label="clear-search" onClick={() => handleSearchChange("")}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
        >
          <DataTable
            customStyles={TABLE_STYLES}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={1}
            paginationResetDefaultPage={resetPage}
            onChangePage={setCurrentPage}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangeRowsPerPage={handlePageSizeChange}
            progressPending={isLoading}
            progressComponent={<Loader />}
            noDataComponent={<Box sx={{ py: 4 }}>No inactive users found</Box>}
            className="custom"
          />
        </Paper>
      </Stack>

      {viewId && (
        <ViewUserManagement show={Boolean(viewId)} onHide={() => setViewId("")} id={viewId} />
      )}
    </Container>
  );
}
